import { UserInfoUserType } from "@/lib/api/interfaces/user-profile.interface";
import { trackProductEvent } from "@/lib/analytics/product-analytics";
import { cn } from "@/lib/utils/cn";
import { Button } from "@/ui-components/ui/button";
import { Context } from "@/util-components/root/context";
import {
  ArrowRight,
  MousePointerClick,
  X,
} from "lucide-react";
import React from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  setOnboardingImportedTemplateId,
  setOnboardingTourStep,
} from "@/util-components/onboarding/tour-step-store";
import {
  OnboardingCompleteDialog,
  OnboardingReadyDialog,
  OnboardingVideoDialog,
} from "@/util-components/onboarding/dialogs";
import type { PlanType } from "@/util-components/onboarding/dialogs";

type DialogStep = "video" | "ready" | "complete" | null;
type TourStepId =
  | "start-home"
  | "start-store-cta"
  | "store-select"
  | "store-customize"
  | "store-import"
  | "templates-home"
  | "create-selection"
  | "create-preview"
  | "create-selection-continue"
  | "create-platforms"
  | "create-schedule"
  | "create-publish";
type TooltipPlacement = "top" | "bottom" | "left" | "right";

interface TourStepConfig {
  id: TourStepId;
  index: number;
  selector: string;
  fallbackSelector?: string;
  interactionSelector?: string;
  scrollAnchorSelector?: string;
  pathPrefix: string;
  title: string;
  description: string;
  helperText?: string;
  nextLabel: string;
  preferredPlacement?: TooltipPlacement;
  highlightPadding?: number;
  manualAdvance?: boolean;
  waitingText?: string;
  restrictInteractionsToTarget?: boolean;
}

const CHANGELOG_SUPPRESS_EVENT = "changelog-suppress";
const SIMULATION_PARAM = "simulateOnboarding";
const SIMULATION_PLAN_PARAM = "simulateOnboardingPlan";
const ONBOARDING_FLOW_STORE_TEMPLATE = "store-template";
const TOTAL_TOUR_STEPS = 12;
const ONBOARDING_STEP_INDEX: Record<Exclude<DialogStep, null>, number> & Record<TourStepId, number> = {
  video: 1,
  ready: 2,
  "start-home": 3,
  "start-store-cta": 4,
  "store-select": 5,
  "store-customize": 6,
  "store-import": 7,
  "templates-home": 8,
  "create-selection": 9,
  "create-preview": 10,
  "create-selection-continue": 11,
  "create-platforms": 12,
  "create-schedule": 13,
  "create-publish": 14,
  complete: 15,
};

const TOUR_STEPS: Record<TourStepId, TourStepConfig> = {
  "start-home": {
    id: "start-home",
    index: 1,
    selector: '[data-onboarding="post-generator-start-page"]',
    scrollAnchorSelector: '[data-onboarding="post-generator-page-header"]',
    pathPrefix: "/post-generator/start",
    title: "Das ist deine Startseite im Post Generator",
    description:
      "Hier findest an einem Ort deine meistgenutzten Templates, geplante Posts und zuletzt veröffentlichte Beiträge.",
    helperText: "So hast du die wichtigsten nächsten Schritte immer direkt im Blick, statt jedes Mal erst in einzelne Bereiche springen zu müssen.",
    nextLabel: "Weiter",
    preferredPlacement: "bottom",
    highlightPadding: 12,
  },
  "start-store-cta": {
    id: "start-store-cta",
    index: 2,
    selector: '[data-onboarding="post-generator-start-store-cta"]',
    scrollAnchorSelector: '[data-onboarding="post-generator-page-header"]',
    pathPrefix: "/post-generator/start",
    title: "Von hier aus kommst du in den Template Store",
    description:
      "Im Template Store findest du fertige Designs, die du in deinen Vereinsbereich importieren kannst. Darüber startest du am schnellsten mit deinem ersten Post.",
    helperText: "Klicke jetzt auf den Button. Danach führen wir dich direkt im Store weiter.",
    nextLabel: "Zum Store",
    preferredPlacement: "bottom",
    highlightPadding: 10,
    manualAdvance: false,
    waitingText: "Klicke jetzt auf „Zum Template Store“.",
    restrictInteractionsToTarget: true,
  },
  "store-select": {
    id: "store-select",
    index: 3,
    selector: '[data-onboarding="store-templates-list"]',
    scrollAnchorSelector: '[data-onboarding="post-generator-page-header"]',
    pathPrefix: "/post-generator/store",
    title: "Wähle ein Template aus dem Store",
    description:
      "Hier findest du alle verfügbaren Store-Templates. Such dir eines aus, das zu deinem Verein passt, und öffne es für den Import.",
    helperText: "Sobald du ein Template auswählst und der Import-Dialog geöffnet ist, geht die Tour automatisch weiter.",
    nextLabel: "Template auswählen",
    preferredPlacement: "bottom",
    highlightPadding: 10,
    manualAdvance: false,
    waitingText: "Wähle jetzt eines der markierten Store-Templates aus.",
    restrictInteractionsToTarget: true,
  },
  "store-customize": {
    id: "store-customize",
    index: 4,
    selector: '[data-onboarding="store-template-import-customize"]',
    pathPrefix: "/post-generator/store",
    title: "Passe das Template an deinen Verein an",
    description:
      "Ergänze hier Vereinsinhalte wie Logos, Bilder oder Texte. Die Vorschau zeigt dir direkt, wie das Template danach für deinen Verein aussieht.",
    helperText: "Prüfe die Angaben in Ruhe. Wenn alles passt, gehst du im nächsten Schritt zum eigentlichen Import.",
    nextLabel: "Weiter",
    preferredPlacement: "right",
    highlightPadding: 12,
    restrictInteractionsToTarget: true,
  },
  "store-import": {
    id: "store-import",
    index: 5,
    selector: '[data-onboarding="store-template-import-confirm"]',
    pathPrefix: "/post-generator/store",
    title: "Importiere das Template",
    description:
      "Wenn die Anpassungen stimmen, importierst du das Template hier in deinen Vereinsbereich. Danach landest du direkt im nächsten Schritt zur Beitragserstellung.",
    nextLabel: "Importieren",
    preferredPlacement: "top",
    highlightPadding: 10,
    manualAdvance: false,
    waitingText: "Wenn alles korrekt ausgewählt ist, importiere das Template jetzt.",
    restrictInteractionsToTarget: true,
  },
  "templates-home": {
    id: "templates-home",
    index: 6,
    selector: '[data-onboarding="customer-templates-page"]',
    interactionSelector: '[data-onboarding="customer-template-highlight"]',
    pathPrefix: "/post-generator/templates",
    title: "Hier siehst du alle Templates deines Vereins",
    description:
      "Nach dem Import landet das Template in deiner eigenen Bibliothek. Von hier aus startest du später neue Posts mit allen Templates deines Vereins.",
    helperText: "Wähle jetzt direkt das eben importierte Template aus. Nur dieses Template ist für den nächsten Schritt relevant.",
    nextLabel: "Template öffnen",
    preferredPlacement: "right",
    highlightPadding: 12,
    manualAdvance: false,
    waitingText: "Klicke jetzt auf dein importiertes Template.",
    restrictInteractionsToTarget: true,
  },
  "create-selection": {
    id: "create-selection",
    index: 7,
    selector: '[data-onboarding="create-post-selection-content"]',
    pathPrefix: "/post-generator/create-post",
    title: "Hier wählst du die Inhalte für dein Template",
    description:
      "Wähle Inhalte wie Spiele, Spieler, Sponsoren, etc. aus. Genau diese Daten erscheinen danach automatisch im Template.",
    helperText: "Fehlende Angaben kannst du direkt hier ergänzen. Sobald alles vollständig ist, gehen wir gemeinsam im nächsten Schritt zur Vorschau.",
    nextLabel: "Weiter",
    preferredPlacement: "bottom",
    highlightPadding: 12,
    restrictInteractionsToTarget: true,
  },
  "create-preview": {
    id: "create-preview",
    index: 8,
    selector: '[data-onboarding="create-post-preview"]',
    pathPrefix: "/post-generator/create-post",
    title: "Hier siehst du deine Live-Vorschau",
    description:
      "Die Vorschau aktualisiert sich direkt mit deinen ausgewählten Inhalten. So prüfst du schon vor dem nächsten Schritt, ob Bild, Texte und Layout für deinen Post passen.",
    nextLabel: "Weiter",
    preferredPlacement: "left",
    highlightPadding: 12,
  },
  "create-selection-continue": {
    id: "create-selection-continue",
    index: 9,
    selector: '[data-onboarding="create-post-selection-continue"]',
    pathPrefix: "/post-generator/create-post",
    title: "Jetzt geht es zur Veröffentlichung",
    description:
      "Klicke jetzt auf „Weiter“. Danach zeigen wir dir direkt die Plattformen und Veröffentlichungsoptionen für deinen Post.",
    nextLabel: "Weiter",
    preferredPlacement: "top",
    highlightPadding: 10,
    manualAdvance: false,
    waitingText: "Klicke jetzt auf „Weiter“, um zu den Veröffentlichungsoptionen zu wechseln.",
    restrictInteractionsToTarget: true,
  },
  "create-platforms": {
    id: "create-platforms",
    index: 10,
    selector: '[data-onboarding="create-post-platforms"]',
    pathPrefix: "/post-generator/create-post",
    title: "Hier wählst du deine Kanäle für den Post",
    description:
      "Lege fest, ob dein Post an Instagram, Facebook oder WhatsApp gesendet wird. Ein Beitrag kann damit direkt auf mehreren Plattformen gleichzeitig genutzt werden.",
    nextLabel: "Weiter",
    preferredPlacement: "left",
    highlightPadding: 12,
  },
  "create-schedule": {
    id: "create-schedule",
    index: 11,
    selector: '[data-onboarding="create-post-schedule"]',
    pathPrefix: "/post-generator/create-post",
    title: "Posts kannst du auch planen",
    description:
      "Mit dieser Funktion planst du Beiträge für einen späteren Zeitpunkt. So bereitest du Content vor und veröffentlichst ihn automatisch genau dann, wenn er gebraucht wird.",
    helperText: "Du kannst deinen Post also entweder sofort veröffentlichen oder direkt im selben Flow einplanen.",
    nextLabel: "Weiter",
    preferredPlacement: "top",
    highlightPadding: 10,
  },
  "create-publish": {
    id: "create-publish",
    index: 12,
    selector: '[data-onboarding="create-post-submit"]',
    pathPrefix: "/post-generator/create-post",
    title: "Hier schickst du deinen Post raus",
    description:
      "Wenn alles passt, veröffentlichst du den Beitrag hier sofort. Damit kommst du mit wenigen Klicks vom Template bis zum fertigen Social-Media-Post.",
    nextLabel: "Fertig",
    preferredPlacement: "top",
    highlightPadding: 10,
  },
};

const normalizePlan = (value: string | null): PlanType => {
  if (value === "pro" || value === "custom") {
    return value;
  }

  return "basic";
};

const emitChangelogSuppression = (suppressed: boolean) => {
  window.dispatchEvent(new CustomEvent(CHANGELOG_SUPPRESS_EVENT, { detail: { suppressed } }));
};

const isRenderableTarget = (target: HTMLElement) => {
  const rect = target.getBoundingClientRect();
  const style = window.getComputedStyle(target);

  return rect.width > 0 && rect.height > 0 && style.visibility !== "hidden" && style.display !== "none";
};

const getTargetBySelector = (selector?: string | null) => {
  if (!selector) {
    return null;
  }

  const directTarget = document.querySelector<HTMLElement>(selector);
  if (directTarget && isRenderableTarget(directTarget)) {
    return directTarget;
  }

  return null;
};

const getSelectorTarget = (config: TourStepConfig): HTMLElement | null => {
  const directTarget = getTargetBySelector(config.selector);
  if (directTarget && isRenderableTarget(directTarget)) {
    return directTarget;
  }

  if (!config.fallbackSelector) {
    return null;
  }

  const fallbackTarget = document.querySelector<HTMLElement>(config.fallbackSelector);

  if (fallbackTarget && isRenderableTarget(fallbackTarget)) {
    return fallbackTarget;
  }

  return null;
};

const getInteractionTarget = (config: TourStepConfig) => {
  const interactionTarget = getTargetBySelector(config.interactionSelector);

  if (interactionTarget) {
    return interactionTarget;
  }

  return getSelectorTarget(config);
};

const getScrollAnchorTarget = (config: TourStepConfig) => {
  if (!config.scrollAnchorSelector) {
    return null;
  }

  const anchorTarget = document.querySelector<HTMLElement>(config.scrollAnchorSelector);

  if (anchorTarget && isRenderableTarget(anchorTarget)) {
    return anchorTarget;
  }

  return null;
};

const getOpenDialogElements = () =>
  Array.from(document.querySelectorAll<HTMLElement>('[role="dialog"][data-state="open"]'));

const shouldSuspendTourOverlay = (target: HTMLElement | null) => {
  const topmostDialog = getOpenDialogElements().at(-1) ?? null;

  if (!topmostDialog) {
    return false;
  }

  return !target || !topmostDialog.contains(target);
};

const isScrollableElement = (element: HTMLElement) => {
  const style = window.getComputedStyle(element);
  const overflowY = style.overflowY;

  return (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") && element.scrollHeight > element.clientHeight;
};

const getScrollableAncestors = (element: HTMLElement) => {
  const ancestors: HTMLElement[] = [];
  let current = element.parentElement;

  while (current) {
    if (isScrollableElement(current)) {
      ancestors.push(current);
    }

    current = current.parentElement;
  }

  return ancestors;
};

const clamp = (value: number, min: number, max: number) => {
  if (max <= min) {
    return min;
  }

  return Math.min(max, Math.max(min, value));
};

const getClippedRect = (rect: DOMRect | null, padding = 10) => {
  if (!rect) {
    return null;
  }

  const viewportInset = 12;
  const rawTop = rect.top - padding;
  const rawLeft = rect.left - padding;
  const rawRight = rect.right + padding;
  const rawBottom = rect.bottom + padding;
  const clippedTop = clamp(rawTop, viewportInset, window.innerHeight - viewportInset);
  const clippedLeft = clamp(rawLeft, viewportInset, window.innerWidth - viewportInset);
  const clippedRight = clamp(rawRight, clippedLeft, window.innerWidth - viewportInset);
  const clippedBottom = clamp(rawBottom, clippedTop, window.innerHeight - viewportInset);

  return {
    top: clippedTop,
    left: clippedLeft,
    width: Math.max(0, clippedRight - clippedLeft),
    height: Math.max(0, clippedBottom - clippedTop),
  };
};

const scrollTargetToTop = (target: HTMLElement, offset = 16) => {
  const scrollableAncestors = getScrollableAncestors(target);

  scrollableAncestors.forEach((ancestor) => {
    const ancestorRect = ancestor.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const nextTop = ancestor.scrollTop + (targetRect.top - ancestorRect.top) - offset;

    ancestor.scrollTo({
      top: Math.max(0, nextTop),
      behavior: "smooth",
    });
  });

  const pageTop = window.scrollY + target.getBoundingClientRect().top - offset;
  window.scrollTo({
    top: Math.max(0, pageTop),
    behavior: "smooth",
  });
};

const getPlacementPriority = (preferredPlacement?: TooltipPlacement): TooltipPlacement[] => {
  const base: TooltipPlacement[] = ["bottom", "top", "right", "left"];

  if (!preferredPlacement) {
    return base;
  }

  return [preferredPlacement, ...base.filter((placement) => placement !== preferredPlacement)];
};

const getTooltipLayout = (
  rect: { top: number; left: number; width: number; height: number },
  cardSize: { width: number; height: number },
  preferredPlacement?: TooltipPlacement
) => {
  const gap = 18;
  const margin = 16;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const boundedCardWidth = Math.min(cardSize.width, viewportWidth - margin * 2);
  const boundedCardHeight = Math.min(cardSize.height, viewportHeight - margin * 2);
  const placements = getPlacementPriority(preferredPlacement);
  const spaces = {
    top: rect.top - margin - gap,
    bottom: viewportHeight - (rect.top + rect.height) - margin - gap,
    left: rect.left - margin - gap,
    right: viewportWidth - (rect.left + rect.width) - margin - gap,
  };

  const centerLeft = rect.left + rect.width / 2 - boundedCardWidth / 2;
  const centerTop = rect.top + rect.height / 2 - boundedCardHeight / 2;

  const coords: Record<TooltipPlacement, { top: number; left: number }> = {
    top: {
      top: rect.top - boundedCardHeight - gap,
      left: clamp(centerLeft, margin, viewportWidth - boundedCardWidth - margin),
    },
    bottom: {
      top: rect.top + rect.height + gap,
      left: clamp(centerLeft, margin, viewportWidth - boundedCardWidth - margin),
    },
    left: {
      top: clamp(centerTop, margin, viewportHeight - boundedCardHeight - margin),
      left: rect.left - boundedCardWidth - gap,
    },
    right: {
      top: clamp(centerTop, margin, viewportHeight - boundedCardHeight - margin),
      left: rect.left + rect.width + gap,
    },
  };

  const fits = (placement: TooltipPlacement) => {
    if (placement === "top" || placement === "bottom") {
      return spaces[placement] >= boundedCardHeight;
    }

    return spaces[placement] >= boundedCardWidth;
  };

  const chosenPlacement = placements.find(fits) || placements.sort((a, b) => spaces[b] - spaces[a])[0];
  const rawCoords = coords[chosenPlacement];

  return {
    placement: chosenPlacement,
    left: clamp(rawCoords.left, margin, viewportWidth - boundedCardWidth - margin),
    top: clamp(rawCoords.top, margin, viewportHeight - boundedCardHeight - margin),
    maxHeight: viewportHeight - margin * 2,
  };
};

const getArrowClassName = (placement: TooltipPlacement) => {
  switch (placement) {
    case "top":
      return "left-1/2 top-full h-4 w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 border-b border-r";
    case "bottom":
      return "left-1/2 top-0 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 border-l border-t";
    case "left":
      return "left-full top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 border-r border-t";
    case "right":
      return "left-0 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 border-b border-l";
  }
};

const TourOverlay = ({
  step,
  onNext,
  onSkip,
}: {
  step: TourStepConfig;
  onNext: () => void;
  onSkip: () => void;
}) => {
  const [isHidden, setIsHidden] = React.useState(false);
  const [isSuspended, setIsSuspended] = React.useState(false);
  const [rect, setRect] = React.useState<DOMRect | null>(null);
  const [interactionRect, setInteractionRect] = React.useState<DOMRect | null>(null);
  const [tooltipSize, setTooltipSize] = React.useState({ width: 340, height: 252 });
  const cardRef = React.useRef<HTMLDivElement | null>(null);
  const targetRef = React.useRef<HTMLElement | null>(null);
  const lastScrolledStepRef = React.useRef<TourStepId | null>(null);

  React.useEffect(() => {
    setIsHidden(false);
    setIsSuspended(false);
  }, [step.id]);

  React.useEffect(() => {
    let frameId: number | null = null;
    let highlightResizeObserver: ResizeObserver | null = null;
    let interactionResizeObserver: ResizeObserver | null = null;

    const syncTarget = () => {
      const target = getSelectorTarget(step);
      const interactionTarget = getInteractionTarget(step);
      targetRef.current = target;
      setIsSuspended(shouldSuspendTourOverlay(target ?? interactionTarget));
      setRect(target?.getBoundingClientRect() ?? null);
      setInteractionRect(interactionTarget?.getBoundingClientRect() ?? null);

      if (highlightResizeObserver) {
        highlightResizeObserver.disconnect();
        highlightResizeObserver = null;
      }

      if (target) {
        highlightResizeObserver = new ResizeObserver(() => {
          setRect(target.getBoundingClientRect());
        });
        highlightResizeObserver.observe(target);
      }

      if (interactionResizeObserver) {
        interactionResizeObserver.disconnect();
        interactionResizeObserver = null;
      }

      if (interactionTarget) {
        interactionResizeObserver = new ResizeObserver(() => {
          setInteractionRect(interactionTarget.getBoundingClientRect());
        });
        interactionResizeObserver.observe(interactionTarget);
      }
    };

    const syncOnNextFrame = () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      frameId = window.requestAnimationFrame(syncTarget);
    };

    syncTarget();

    const mutationObserver = new MutationObserver(syncOnNextFrame);
    mutationObserver.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["class", "style", "data-onboarding", "data-state"],
    });

    const intervalId = window.setInterval(syncTarget, 250);

    window.addEventListener("resize", syncOnNextFrame);
    window.addEventListener("scroll", syncOnNextFrame, true);

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      if (highlightResizeObserver) {
        highlightResizeObserver.disconnect();
      }
      if (interactionResizeObserver) {
        interactionResizeObserver.disconnect();
      }
      mutationObserver.disconnect();
      window.clearInterval(intervalId);
      window.removeEventListener("resize", syncOnNextFrame);
      window.removeEventListener("scroll", syncOnNextFrame, true);
    };
  }, [step]);

  React.useEffect(() => {
    const target = targetRef.current;
    const scrollAnchorTarget = getScrollAnchorTarget(step);

    if (!target || rect === null || lastScrolledStepRef.current === step.id) {
      return;
    }

    lastScrolledStepRef.current = step.id;

    window.requestAnimationFrame(() => {
      scrollTargetToTop(scrollAnchorTarget ?? target);
    });
  }, [rect, step]);

  React.useLayoutEffect(() => {
    if (!cardRef.current) {
      return;
    }

    const nextSize = {
      width: cardRef.current.offsetWidth,
      height: cardRef.current.offsetHeight,
    };

    if (nextSize.width !== tooltipSize.width || nextSize.height !== tooltipSize.height) {
      setTooltipSize(nextSize);
    }
  }, [step, tooltipSize.height, tooltipSize.width]);

  if (!rect || isHidden || isSuspended) {
    return null;
  }

  const highlightPadding = step.highlightPadding ?? 10;
  const highlightRect = getClippedRect(rect, highlightPadding);

  if (!highlightRect) {
    return null;
  }

  const allowedInteractionRect = getClippedRect(interactionRect, highlightPadding);
  const tooltipAnchorRect = step.restrictInteractionsToTarget && allowedInteractionRect ? allowedInteractionRect : highlightRect;
  const tooltipLayout = getTooltipLayout(tooltipAnchorRect, tooltipSize, step.preferredPlacement);

  return createPortal(
    <div className="fixed inset-0 z-[120] pointer-events-none">
      {step.restrictInteractionsToTarget ? (
        allowedInteractionRect ? (
          <>
            <div
              className="absolute inset-x-0 top-0 pointer-events-auto"
              style={{ height: allowedInteractionRect.top }}
            />
            <div
              className="absolute left-0 pointer-events-auto"
              style={{
                top: allowedInteractionRect.top,
                width: allowedInteractionRect.left,
                height: allowedInteractionRect.height,
              }}
            />
            <div
              className="absolute right-0 pointer-events-auto"
              style={{
                top: allowedInteractionRect.top,
                width: Math.max(0, window.innerWidth - (allowedInteractionRect.left + allowedInteractionRect.width)),
                height: allowedInteractionRect.height,
              }}
            />
            <div
              className="absolute inset-x-0 bottom-0 pointer-events-auto"
              style={{
                top: allowedInteractionRect.top + allowedInteractionRect.height,
              }}
            />
          </>
        ) : (
          <div className="absolute inset-0 pointer-events-auto" />
        )
      ) : null}

      <div
        className="absolute rounded-[28px] border border-white/80 bg-transparent shadow-[0_0_0_9999px_rgba(15,23,42,0.56)] transition-all duration-200"
        style={highlightRect}
      />

      <div
        ref={cardRef}
        className="absolute pointer-events-auto w-[min(22rem,calc(100vw-2rem))] overflow-visible"
        style={{
          left: tooltipLayout.left,
          top: tooltipLayout.top,
          maxHeight: tooltipLayout.maxHeight,
        }}
      >
        <div className={cn("absolute border-slate-200 bg-white", getArrowClassName(tooltipLayout.placement))} />

        <div className="flex max-h-[calc(100vh-2rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl" style={{ maxHeight: tooltipLayout.maxHeight }}>
          <div className="flex items-start justify-between gap-4 border-b border-slate-100 bg-white/95 px-5 py-4 backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-50">
                <MousePointerClick className="h-4 w-4 text-indigo-600" />
              </div>
              <div className="text-xs font-semibold uppercase tracking-wider text-indigo-600">
                Schritt {step.index} von {TOTAL_TOUR_STEPS}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsHidden(true)}
              className="rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 -mr-1.5 -mt-1.5"
              title="Tour-Tipp für diesen Schritt ausblenden"
              aria-label="Hinweis ausblenden"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-bold tracking-tight text-gray-900">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-600">{step.description}</p>
              </div>

              {step.helperText && (
                <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 px-3 py-2 text-xs font-medium leading-relaxed text-indigo-800">
                  {step.helperText}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/50 p-4 rounded-b-2xl">
            <button
              type="button"
              onClick={onSkip}
              className="text-sm font-medium text-gray-500 transition-colors hover:text-red-600"
              title="Beendet die Tour vorzeitig"
            >
              Tour abbrechen
            </button>
            {step.manualAdvance === false ? (
              <div className="max-w-[14rem] text-right text-xs font-medium leading-relaxed text-slate-500">
                {step.waitingText || "Führe jetzt den markierten Schritt aus."}
              </div>
            ) : (
              <Button onClick={onNext} className="rounded-xl bg-indigo-600 px-4 font-medium text-white shadow-md shadow-indigo-600/10 hover:bg-indigo-700">
                {step.nextLabel}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export const OnboardingManager = () => {
  const { user } = React.useContext(Context);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dialogStep, setDialogStep] = React.useState<DialogStep>(null);
  const [tourStep, setTourStep] = React.useState<TourStepId | null>(null);
  const [selectedPlan, setSelectedPlan] = React.useState<PlanType>("basic");
  const [videoEnded, setVideoEnded] = React.useState(false);
  const [videoError, setVideoError] = React.useState(false);
  const [releaseScheduled, setReleaseScheduled] = React.useState(false);
  const [pendingChangelogRelease, setPendingChangelogRelease] = React.useState(false);
  const releaseTimeoutRef = React.useRef<number | null>(null);
  const trackedStepViewsRef = React.useRef<Set<string>>(new Set());
  const highestTrackedStepIndexRef = React.useRef<number>(0);

  const isCustomer = user?.userType === UserInfoUserType.CUSTOMER_USER;
  const isCreatePostPath = location.pathname.startsWith("/post-generator/create-post");
  const isStoreTemplatesPath = location.pathname.startsWith("/post-generator/store");
  const isCustomerTemplatesPath = location.pathname.startsWith("/post-generator/templates");
  const tourConfig = tourStep ? TOUR_STEPS[tourStep] : null;
  const shouldSuppressChangelog = isCustomer && (dialogStep !== null || tourStep !== null || pendingChangelogRelease || releaseScheduled);
  React.useEffect(() => {
    setOnboardingTourStep(tourStep);
  }, [tourStep]);

  React.useEffect(() => {
    return () => {
      setOnboardingTourStep(null);
    };
  }, []);

  const clearTransientQueryParams = React.useCallback(
    (keys: string[]) => {
      const nextParams = new URLSearchParams(searchParams);
      let changed = false;

      keys.forEach((key) => {
        if (nextParams.has(key)) {
          nextParams.delete(key);
          changed = true;
        }
      });

      if (changed) {
        setSearchParams(nextParams, { replace: true });
      }
    },
    [searchParams, setSearchParams]
  );

  const resetFlow = React.useCallback(
    (plan: PlanType) => {
      trackedStepViewsRef.current = new Set();
      highestTrackedStepIndexRef.current = 0;
      setOnboardingImportedTemplateId(null);
      setSelectedPlan(plan);
      setDialogStep("video");
      setTourStep(null);
      setPendingChangelogRelease(false);
      setReleaseScheduled(false);
      setVideoEnded(false);
      setVideoError(false);
    },
    []
  );

  React.useEffect(() => {
    if (!isCustomer) {
      return;
    }

    const activeStep = tourStep ?? dialogStep;
    if (!activeStep) {
      return;
    }

    const stepIndex = ONBOARDING_STEP_INDEX[activeStep];
    if (!stepIndex) {
      return;
    }

    if (!trackedStepViewsRef.current.has(activeStep)) {
      trackedStepViewsRef.current.add(activeStep);

      trackProductEvent("frontend.onboarding.step_viewed", {
        plan: selectedPlan,
        step_id: activeStep,
        step_index: stepIndex,
        step_type: tourStep ? "tour" : "dialog",
        flow: ONBOARDING_FLOW_STORE_TEMPLATE,
      });
    }

    if (stepIndex > highestTrackedStepIndexRef.current) {
      highestTrackedStepIndexRef.current = stepIndex;

      trackProductEvent("frontend.onboarding.progress_reached", {
        plan: selectedPlan,
        step_id: activeStep,
        step_index: stepIndex,
        step_type: tourStep ? "tour" : "dialog",
        flow: ONBOARDING_FLOW_STORE_TEMPLATE,
      });
    }
  }, [dialogStep, isCustomer, selectedPlan, tourStep]);

  const startTour = React.useCallback(() => {
    setOnboardingImportedTemplateId(null);
    setDialogStep(null);
    setTourStep("start-home");
    navigate("/post-generator/start");
  }, [navigate]);

  const releaseChangelogWithDelay = React.useCallback(() => {
    setPendingChangelogRelease(true);
  }, []);

  const finishTour = React.useCallback(() => {
    setOnboardingImportedTemplateId(null);
    setTourStep(null);
    setDialogStep("complete");
  }, []);

  const skipTour = React.useCallback(() => {
    setOnboardingImportedTemplateId(null);
    setTourStep(null);
    setDialogStep("complete");
  }, []);

  const handleCompleteDialogCta = React.useCallback(() => {
    setDialogStep(null);
    releaseChangelogWithDelay();
    navigate("/");
  }, [navigate, releaseChangelogWithDelay]);

  React.useEffect(() => {
    if (!isCustomer) {
      return;
    }

    if (searchParams.get("success") === "true") {
      const plan = normalizePlan(searchParams.get("plan"));
      clearTransientQueryParams(["success", "plan"]);
      resetFlow(plan);
      return;
    }

    const simulation = searchParams.get(SIMULATION_PARAM);
    if (!simulation) {
      return;
    }

    const simulatedPlan = normalizePlan(searchParams.get(SIMULATION_PLAN_PARAM));
    clearTransientQueryParams([SIMULATION_PARAM, SIMULATION_PLAN_PARAM]);

    if (simulation === "tour") {
      setOnboardingImportedTemplateId(null);
      setSelectedPlan(simulatedPlan);
      setDialogStep(null);
      setTourStep("start-home");
      setPendingChangelogRelease(false);
      navigate("/post-generator/start");
      return;
    }

    resetFlow(simulatedPlan);
  }, [clearTransientQueryParams, isCustomer, navigate, resetFlow, searchParams]);

  React.useEffect(() => {
    emitChangelogSuppression(shouldSuppressChangelog);

    return () => {
      emitChangelogSuppression(false);
    };
  }, [shouldSuppressChangelog]);

  React.useEffect(() => {
    if (releaseTimeoutRef.current) {
      window.clearTimeout(releaseTimeoutRef.current);
      releaseTimeoutRef.current = null;
    }

    if (!pendingChangelogRelease) {
      setReleaseScheduled(false);
      return;
    }

    if (isCreatePostPath) {
      setReleaseScheduled(false);
      return;
    }

    setReleaseScheduled(true);
    releaseTimeoutRef.current = window.setTimeout(() => {
      setPendingChangelogRelease(false);
      setReleaseScheduled(false);
      releaseTimeoutRef.current = null;
    }, 3000);

    return () => {
      if (releaseTimeoutRef.current) {
        window.clearTimeout(releaseTimeoutRef.current);
        releaseTimeoutRef.current = null;
      }
    };
  }, [isCreatePostPath, pendingChangelogRelease]);

  React.useEffect(() => {
    if (!videoEnded) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setDialogStep("ready");
    }, 350);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [videoEnded]);

  React.useEffect(() => {
    if (!tourStep) {
      return;
    }

    if (tourStep === "start-store-cta" && isStoreTemplatesPath) {
      setTourStep("store-select");
      return;
    }

    if ((tourStep === "store-customize" || tourStep === "store-import") && isStoreTemplatesPath && !getSelectorTarget(TOUR_STEPS["store-customize"])) {
      setTourStep("store-select");
      return;
    }

    if (tourStep === "templates-home" && isCustomerTemplatesPath && !getTargetBySelector(TOUR_STEPS["templates-home"].interactionSelector)) {
      return;
    }

    if (tourStep === "start-home" || tourStep === "start-store-cta") {
      return;
    }

    if (tourStep === "store-select") {
      const interval = window.setInterval(() => {
        const customizeTarget = getSelectorTarget(TOUR_STEPS["store-customize"]);

        if (customizeTarget) {
          setTourStep("store-customize");
        }
      }, 200);

      return () => {
        window.clearInterval(interval);
      };
    }

    if (tourStep === "store-import" && isCustomerTemplatesPath) {
      setTourStep("templates-home");
      return;
    }

    if (tourStep === "templates-home" && isCreatePostPath) {
      setTourStep("create-selection");
      return;
    }

    if (tourStep === "store-customize" && isCreatePostPath) {
      setTourStep("create-selection");
      return;
    }

    if (tourStep === "templates-home") {
      return;
    }

    if (tourStep === "create-selection-continue") {
      const interval = window.setInterval(() => {
        const platformsTarget = getSelectorTarget(TOUR_STEPS["create-platforms"]);

        if (platformsTarget) {
          setTourStep("create-platforms");
        }
      }, 200);

      return () => {
        window.clearInterval(interval);
      };
    }

    return;
  }, [isCreatePostPath, isCustomerTemplatesPath, isStoreTemplatesPath, tourStep]);

  const handleTourNext = React.useCallback(() => {
    if (!tourStep) {
      return;
    }

    switch (tourStep) {
      case "start-home":
        setTourStep("start-store-cta");
        break;
      case "start-store-cta":
        break;
      case "store-select":
        break;
      case "store-customize":
        setTourStep("store-import");
        break;
      case "store-import":
        break;
      case "templates-home":
        break;
      case "create-selection":
        setTourStep("create-preview");
        break;
      case "create-preview":
        setTourStep("create-selection-continue");
        break;
      case "create-selection-continue":
        setTourStep("create-platforms");
        break;
      case "create-platforms":
        setTourStep("create-schedule");
        break;
      case "create-schedule":
        setTourStep("create-publish");
        break;
      case "create-publish":
        finishTour();
        break;
    }
  }, [finishTour, tourStep]);

  if (!isCustomer) {
    return null;
  }

  return (
    <>
      <OnboardingVideoDialog
        open={dialogStep === "video"}
        plan={selectedPlan}
        onSkip={() => setDialogStep("ready")}
        onVideoEnd={() => setVideoEnded(true)}
        onVideoError={() => setVideoError(true)}
        videoEnded={videoEnded}
        videoError={videoError}
      />

      <OnboardingReadyDialog open={dialogStep === "ready"} plan={selectedPlan} onStartTour={startTour} />

      {tourConfig && location.pathname.startsWith(tourConfig.pathPrefix) && (
        <TourOverlay step={tourConfig} onNext={handleTourNext} onSkip={skipTour} />
      )}

      <OnboardingCompleteDialog
        open={dialogStep === "complete"}
        plan={selectedPlan}
        onCloseSettingsHint={() => setDialogStep(null)}
        onComplete={handleCompleteDialogCta}
      />
    </>
  );
};
